import React, { useState } from "react";
import { Plus, Users, UserCheck } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "../../lib/api";
import { QuickList, inputCls } from "./QuickList";

function useCrud(path, setItems) {
  const add = async (data) => {
    try { const res = await api.post(path, { data }); setItems((x) => [res.data, ...x]); return true; }
    catch (e) { toast.error(errMsg(e)); return false; }
  };
  const remove = async (it) => {
    try { await api.delete(`${path}/${it.id}`); setItems((x) => x.filter((y) => y.id !== it.id)); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return { add, remove };
}

function TeammatesBlock({ roles, teammates, setTeammates }) {
  const [f, setF] = useState({ name: "", role_id: "", location: "" });
  const { add, remove } = useCrud("/teammates", setTeammates);
  const submit = async (e) => {
    e.preventDefault();
    if (!f.name.trim()) return;
    const role = roles.find((r) => r.id === f.role_id);
    if (await add({ name: f.name.trim(), role_id: role?.id || null, role_name: role?.name || null, location: f.location, phone: "", other_details: "" })) setF({ name: "", role_id: "", location: "" });
  };
  return (
    <section className="mb-5">
      <h3 className="flex items-center gap-2 text-xs uppercase tracking-widest text-blue-300/80 mb-2"><Users size={13} /> Teammates</h3>
      <QuickList items={teammates} onDelete={remove} testId="wizard-teammates-list" empty="No teammates yet."
        render={(t) => <><span className="font-medium">{t.name}</span><span className="text-slate-500">{t.role_name ? ` · ${t.role_name}` : ""}{t.location ? ` · ${t.location}` : ""}</span></>} />
      <form onSubmit={submit} className="grid grid-cols-1 sm:grid-cols-[1fr_1fr_1fr_auto] gap-2">
        <input className={inputCls} placeholder="Name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="wizard-teammate-name" />
        <select className={inputCls} value={f.role_id} onChange={(e) => setF({ ...f, role_id: e.target.value })} data-testid="wizard-teammate-role">
          <option value="">Role (optional)</option>
          {roles.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
        </select>
        <input className={inputCls} placeholder="Location (optional)" value={f.location} onChange={(e) => setF({ ...f, location: e.target.value })} data-testid="wizard-teammate-location" />
        <button type="submit" data-testid="wizard-teammate-add" className="inline-flex items-center justify-center gap-1 bg-blue-600 hover:bg-blue-500 text-white px-3 py-2 rounded-lg text-sm"><Plus size={14} /> Add</button>
      </form>
    </section>
  );
}

function StakeholdersBlock({ stakeholders, setStakeholders }) {
  const [f, setF] = useState({ name: "", email: "", organization: "" });
  const { add, remove } = useCrud("/stakeholders", setStakeholders);
  const submit = async (e) => {
    e.preventDefault();
    if (!f.name.trim()) return;
    if (await add({ name: f.name.trim(), email: f.email.trim(), organization: f.organization.trim() })) setF({ name: "", email: "", organization: "" });
  };
  return (
    <section>
      <h3 className="flex items-center gap-2 text-xs uppercase tracking-widest text-blue-300/80 mb-2"><UserCheck size={13} /> Stakeholders</h3>
      <QuickList items={stakeholders} onDelete={remove} testId="wizard-stakeholders-list" empty="No stakeholders yet."
        render={(s) => <><span className="font-medium">{s.name}</span><span className="text-slate-500">{s.organization ? ` · ${s.organization}` : ""}{s.email ? ` · ${s.email}` : ""}</span></>} />
      <form onSubmit={submit} className="grid grid-cols-1 sm:grid-cols-[1fr_1fr_1fr_auto] gap-2">
        <input className={inputCls} placeholder="Name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="wizard-stakeholder-name" />
        <input className={inputCls} type="email" placeholder="Email (optional)" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="wizard-stakeholder-email" />
        <input className={inputCls} placeholder="Organization (optional)" value={f.organization} onChange={(e) => setF({ ...f, organization: e.target.value })} data-testid="wizard-stakeholder-org" />
        <button type="submit" data-testid="wizard-stakeholder-add" className="inline-flex items-center justify-center gap-1 bg-blue-600 hover:bg-blue-500 text-white px-3 py-2 rounded-lg text-sm"><Plus size={14} /> Add</button>
      </form>
    </section>
  );
}

export default function PeopleStep(props) {
  return (
    <div data-testid="wizard-step-people">
      <p className="text-sm text-slate-400 mb-3">Add the people doing the work and the stakeholders who need visibility.</p>
      <TeammatesBlock {...props} />
      <StakeholdersBlock {...props} />
    </div>
  );
}
